import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_KEY = 'archive:scroll';
const RESTORE_TIMEOUT_MS = 4000;

/** Scroll offsets by history-entry key, so Back/Forward can return to them. */
const positions: Map<string, number> = (() => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return new Map(Object.entries(raw ? (JSON.parse(raw) as Record<string, number>) : {}));
  } catch {
    return new Map();
  }
})();

let persistTimer: number | undefined;
function persistSoon() {
  window.clearTimeout(persistTimer);
  persistTimer = window.setTimeout(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(positions)));
    } catch {
      /* non-fatal */
    }
  }, 250);
}

/** True while we are programmatically restoring, so we don't record the ramp-up. */
let restoring = false;

/**
 * Did the browser load this page fresh (typed URL, link, reload) rather than
 * returning to it with Back/Forward? Only the latter should restore a scroll
 * position; a fresh visit should always open on the first screen.
 */
function isFreshLoad(): boolean {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    return !nav || nav.type !== 'back_forward';
  } catch {
    return true;
  }
}

/** Set once the very first route of this page load has been handled. */
let bootHandled = false;

/**
 * Scroll handling for the whole app:
 *  - New page (push/replace to a different path): start at the top.
 *  - Back/Forward: return to exactly where that history entry was left,
 *    waiting for the page's content to load tall enough if needed.
 *  - Same-path changes (e.g. picking a topic filter): leave scroll alone.
 */
export function ScrollToTop() {
  const { pathname, key } = useLocation();
  const navType = useNavigationType();
  const lastPath = useRef<string | null>(null);

  // Take over from the browser's own restoration, which fires before our
  // async content has rendered.
  useLayoutEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  }, []);

  // Record the scroll offset for the current history entry. A layout effect so
  // the listener is swapped synchronously in the same commit as the route
  // change, before the browser can report a clamped scroll on the new page.
  useLayoutEffect(() => {
    const onScroll = () => {
      if (restoring) return;
      positions.set(key, window.scrollY);
      persistSoon();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [key]);

  useLayoutEffect(() => {
    // The first route of a page load reports as "POP", and its history key is
    // the same one a previous visit saved a scroll offset under. On a fresh
    // load or reload that stale offset must not be restored (it dropped phone
    // visitors partway down the page), so open at the top instead.
    const firstRun = !bootHandled;
    bootHandled = true;
    if (firstRun && isFreshLoad()) {
      positions.delete(key);
      persistSoon();
      window.scrollTo(0, 0);
      return;
    }

    const saved = positions.get(key);

    if (navType === 'POP' && saved && saved > 0) {
      restoring = true;
      const start = performance.now();
      let raf = 0;

      const stop = () => {
        restoring = false;
        cancelAnimationFrame(raf);
        window.removeEventListener('wheel', stop);
        window.removeEventListener('touchmove', stop);
        window.removeEventListener('keydown', stop);
      };

      const attempt = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo(0, Math.min(saved, Math.max(max, 0)));
        // Done once the page is tall enough, or we've waited long enough.
        if (max >= saved || performance.now() - start > RESTORE_TIMEOUT_MS) {
          restoring = false;
          return;
        }
        raf = requestAnimationFrame(attempt);
      };

      // If the reader starts scrolling themselves, get out of the way.
      window.addEventListener('wheel', stop, { passive: true });
      window.addEventListener('touchmove', stop, { passive: true });
      window.addEventListener('keydown', stop);

      attempt();
      return stop;
    }

    if (navType === 'POP') {
      // Back/Forward to an entry we never recorded: start at the top.
      window.scrollTo(0, 0);
    } else if (lastPath.current !== pathname) {
      // Push/replace to a different page: start at the top. Same-path changes
      // (e.g. picking a ?tag= filter) leave the scroll position alone.
      window.scrollTo(0, 0);
    }
  }, [key, navType, pathname]);

  // Track the previous path in an effect that runs after the one above.
  useEffect(() => {
    lastPath.current = pathname;
  }, [pathname]);

  return null;
}
